// import { usePossibleChildTrees, usePossibleParentTrees } from "@hooks/usePossibleChildTrees";
import React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  useBackgroundTrees,
  useBackgroundSensors,
  useBackgroundTreeSpecies,
  usePossibleChildTrees,
  usePossibleParentTrees,
  useBackgroundCustomerBasicInfo,
} from "@hooks/useBackgroundData";

/**
 * Component that initializes background data queries
 * This runs within the QueryClientProvider context to pre-load data
 * Now with additional authentication and route protection
 */
const BackgroundDataLoader = () => {
  const location = useLocation();
  const isOnLoginPage = location.pathname === "/login";
  const authState = useSelector((state) => state.auth);
  const isAuthenticated = !!(authState?.authToken && authState?.user);

  // Chrome-specific: Check for rehydration stability
  const isChrome = typeof window !== "undefined" && !!window.chrome;
  const [isRehydrated, setIsRehydrated] = React.useState(!isChrome); // Non-Chrome browsers don't need to wait

  React.useEffect(() => {
    if (isChrome && authState) {
      // Small delay for Chrome to ensure Redux persist has fully rehydrated
      const timer = setTimeout(() => {
        setIsRehydrated(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isChrome, authState]);

  // Determine if we should enable background loading
  const shouldLoadData = isAuthenticated && !isOnLoginPage && authState && isRehydrated;

  console.debug("[BackgroundDataLoader]", {
    shouldLoadData,
    isAuthenticated,
    isOnLoginPage,
    hasAuthState: !!authState,
    isRehydrated,
    browser: isChrome ? "Chrome" : "Other",
  });

  // ALWAYS call hooks in the same order - pass enabled flag to control execution
  // Initialize trees list query with low priority (background loading)
  // This data will be cached and available for tree selection components
  // DISABLED: Now using server-side pagination instead
  // useBackgroundTrees(shouldLoadData);

  // Initialize sensors list query with low priority (background loading)
  // This data will be cached and available for sensor selection components
  // DISABLED: Now using server-side pagination instead
  // useBackgroundSensors(shouldLoadData);

  // DISABLED: Tree species can cause performance issues on large datasets
  // This should be loaded on-demand on tree-related pages only, not globally
  // Initialize tree species list query with low priority (background loading)
  // This data will be cached and available for tree form components
  // useBackgroundTreeSpecies(shouldLoadData);

  // DISABLED: Possible child/parent trees can cause performance issues on large datasets
  // These should be loaded on-demand when needed, not globally
  // Initialize possible child trees query with low priority (background loading)
  // This data will be cached and available when updating trees
  // usePossibleChildTrees(shouldLoadData);

  // Initialize possible parent trees query with low priority (background loading)
  // This data will be cached and available when managing tree relationships
  // usePossibleParentTrees(shouldLoadData);

  // Initialize customer basic info query with low priority (background loading)
  // This data will be cached and available for header/customer display
  useBackgroundCustomerBasicInfo(shouldLoadData);

  // Return null since this is just for data loading
  return null;
};

export default BackgroundDataLoader;
